import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { LinearProgress, withStyles, withWidth } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { DateTime } from "luxon";
import moment from "moment";
import { useParams, useHistory } from "react-router";
import { useTranslation } from "react-i18next";

import { withSnackbar } from "../../../HOC";
import FullScreenWidget from "../../../components/Layout/FullScreenWidget";
import LastRefreshed from "../../../components/Layout/LastRefreshed";
import { FleetContext } from "../../../context";
import { DateService } from "../../../services/dates.service";
import { DateRangeChips } from "../../../components/FormElements";
import DiagnosticModal, { ModalContext } from "../Modals/DiagnosticModal/DiagnosticModal";
import VehicleHistoryMapContainer from "./VehicleHistoryMapContainer";
import VehicleHistoryTimeline from "./VehicleHistoryTimeline";
import VehicleHistorySubheader from "./subcomponents/VehicleHistorySubheader";
import { vehicleHistoryStyles } from "./subcomponents/sharedStyles";
import DiagnosticModalContext from "../Modals/DiagnosticModal/DiagnosticModalContext";
import { accessTokenCookieKey, getCookies, refreshTokenCookieKey } from "../../../API/cookies";

export const VehicleHistoryWidgetContainer = ({
  classes,
  width,
  vehicle,
  loadingTimeline,
  setSnackbarMessage,
  timelineStatusesByDate,
  tripInfo,
}) => {
  const {
    sanitizeEvent,
    activeFleet,
    activeVehicle,
    fetchGeofences,
    minTimelineDate,
    maxTimelineDate,
    setMinTimelineDate,
    setMaxTimelineDate,
    fetchVehicleTimeline,
  } = useContext(FleetContext);
  const { getDiagnostics } = useContext(DiagnosticModalContext);
  const { vehicleId } = useParams();
  const history = useHistory();
  const [visibleEvents, setVisibleEvents] = useState([]);
  const [modalContent, setModalContent] = useState([]);
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingTimelineSemaphore, setLoadingTimelineSemaphore] = useState(0);
  const [pollCount, setPollCount] = useState(0);
  const [retryCounter, setRetryCounter] = useState(0);
  const { t } = useTranslation();

  const today = DateTime.now().toFormat("yyyy-MM-dd");
  const twoWeeksAgo = DateTime.now().minus({ weeks: 2 }).toFormat("yyyy-MM-dd");
  const fourWeeksAgo = DateTime.now().minus({ weeks: 4 }).toFormat("yyyy-MM-dd");

  const [minDate, setMinDate] = useState(twoWeeksAgo);
  const [maxDate, setMaxDate] = useState(today);

  const fetchTimeline = async (shouldModifyLoadingSemaphore) => {
    if (shouldModifyLoadingSemaphore) setLoadingTimelineSemaphore((val) => val + 1);
    await fetchVehicleTimeline(vehicleId);
    if (shouldModifyLoadingSemaphore) setLoadingTimelineSemaphore((val) => val - 1);
  };

  useEffect(() => {
    const selfCheckMode = activeVehicle && activeVehicle.target_state === "self_check";
    const interval = selfCheckMode ? 5000 : 15000;
    const pollingTimer = setInterval(() => setPollCount((count) => count + 1), interval);

    return () => {
      clearInterval(pollingTimer);
    };
  }, []);

  useEffect(() => {
    const loadMap = async () => {
      setLoadingMap(true);
      await fetchGeofences(activeFleet.uuid4);
      setLoadingMap(false);
    };

    loadMap();
  }, []);

  useEffect(() => {
    const newMinDate = moment(minDate);
    if (newMinDate.isValid()) setMinTimelineDate(newMinDate.toISOString().replace("Z", ""));
  }, [minDate]);

  useEffect(() => {
    const newMaxDate = moment(maxDate);
    if (newMaxDate.isValid())
      setMaxTimelineDate(newMaxDate.add(1, "day").toISOString().replace("Z", ""));
  }, [maxDate]);

  useEffect(() => {
    if (!maxDate) setMaxDate(today);
    if (!minDate) setMinDate(twoWeeksAgo);
  }, [maxDate, minDate]);

  useEffect(() => {
    fetchTimeline(true);
  }, [minTimelineDate, maxTimelineDate]);

  const showEventOnMap = (event) => setVisibleEvents([event]);

  const handleModalClose = () => () => setDiagnostics();

  const setEventDiagnostics = (eventList) => async () => {
    const eventPromises = eventList.map((event) => event.toRawLog());
    // Take promises and turn them into actual data
    try {
      const diagnostics = (await Promise.all(eventPromises)).map((event) => event[0]);
      setDiagnostics(diagnostics);
    } catch (error) {
      setSnackbarMessage(t("Vehicles.Detail.History.TripLogError"));
      console.error(error);
    }
  };

  const setDiagnostics = (diagnostics = []) => {
    const events = diagnostics.map((event) => sanitizeEvent(event));
    setModalContent(events);
  };

  const isMobile = width === "sm" || width === "xs";
  const modalOpen = modalContent.length > 0;

  const onShowPathChange = (events) => setVisibleEvents(events);

  const pollVehicle = async (countRetries = false) => {
    if (!getCookies(accessTokenCookieKey) && !getCookies(refreshTokenCookieKey))
      history.push("/logout");
    fetchTimeline(loadingTimelineSemaphore > 0);
    getDiagnostics();
    if (countRetries) setRetryCounter(retryCounter + 1);
  };

  useEffect(() => {
    const selfCheckMode = activeVehicle && activeVehicle.target_state === "self_check";
    pollVehicle(selfCheckMode);
  }, [pollCount]);

  return (
    <FullScreenWidget
      title="History"
      cardContentStyle={{ paddingTop: 0 }}
      subheader={
        Object.keys(tripInfo).length > 0 && <VehicleHistorySubheader tripInfo={tripInfo} />
      }
      cardHeaderProps={{
        style: {
          marginBottom: isMobile ? 40 : null,
        },
      }}
    >
      <ModalContext.Provider
        value={{
          setDiagnostics: (events) => setEventDiagnostics(events),
        }}
      >
        <DateRangeChips
          floor={minDate}
          ceil={maxDate}
          onChangeFloor={setMinDate}
          onChangeCeil={setMaxDate}
          minDate={fourWeeksAgo}
          maxDate={today}
        />
        {loadingTimeline ? (
          <CircularProgress />
        ) : (
          <div className={classes.detailsContainer}>
            <VehicleHistoryTimeline
              entriesByDate={timelineStatusesByDate}
              handleClick={showEventOnMap}
              visibleEventTimestamp={visibleEvents.length ? visibleEvents[0].timestamp : null}
              onShowPathChange={onShowPathChange}
              visibleEvents={visibleEvents}
              updating={loadingTimelineSemaphore !== 0}
            />
            <div
              className={classes.mapContainer}
              style={isMobile ? {} : { position: "absolute", top: 10, right: 10 }}
            >
              {loadingMap && <LinearProgress color="secondary" />}
              {!isMobile && (
                <LastRefreshed
                  handleClick={pollVehicle}
                  time={DateService.convert(vehicle.last_ingest)}
                />
              )}
              <VehicleHistoryMapContainer
                mapHeight={isMobile ? 300 : 900}
                vehiclePosition={vehicle.last_position}
                visibleEvents={visibleEvents}
              />
            </div>
          </div>
        )}
        <DiagnosticModal
          modalOpen={modalOpen}
          handleModalClose={handleModalClose}
          sortedDiagnostics={modalContent}
        />
      </ModalContext.Provider>
    </FullScreenWidget>
  );
};

VehicleHistoryWidgetContainer.propTypes = {
  loadingTimeline: PropTypes.bool,
  vehicle: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  timelineStatusesByDate: PropTypes.object.isRequired,
  tripInfo: PropTypes.object.isRequired,
};

VehicleHistoryWidgetContainer.defaultProps = {
  loadingTimeline: false,
};

export default withStyles(vehicleHistoryStyles)(
  withWidth()(withSnackbar(VehicleHistoryWidgetContainer)),
);
